import React, { useEffect, useState } from 'react';
import * as Yup from "yup";
import Breadcrumbs from '../../components/BreadCrumbs';
import { postOrder } from '../../services/orders';
import { toast } from 'react-toastify';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Form, Formik } from 'formik';
import Button from '../../components/buttons/Button';
import InputField from '../../components/inputFields/InputField';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import ImagePicker from '../../components/inputFields/ImagePicker';
import AsyncFormikDropdown from '../../components/inputFields/AsyncFormikDropDown';
import { searchUser } from '../../services/search';
import { getCategories, patchShop, postShop } from '../../services/shop';
import { callPincodeApi, applyPincodeToForm } from '../../services/pincode';
import { sanitizeMobile } from '../../utils/phone';

function ShopForm() {
    const location = useLocation();
    const navigate = useNavigate();
    const [rowData, setRowData] = useState(null);
    const [userOptions, setUserOptions] = useState([]);
    const [logo, setLogo] = useState(null);
    const [images, setImages] = useState([{ id: Date.now(), file: null }]);
    const [imagesToRemove, setImagesToRemove] = useState([]);
    const [dealsInOptions, setDealsInOptions] = useState([]);
    const [selectedDealsIn, setSelectedDealsIn] = useState([]);

    const toggleSelection = (id) => {
        setSelectedDealsIn((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
    };

    // Function to handle adding a new image slot
    const handleAddImage = async () => {
        const id = Date.now();
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = (e) => {
            const file = e.target.files[0];
            setImages([...images, { id: id, file: file }]);
        };
        input.click();
    };

    // Function to handle selecting an image
    const handleImageSelect = (id, file) => {
        var newList = imagesToRemove;
        if (typeof id === "string") {
            if (!newList.includes(id)) {
                newList.push(id);
            }
            setImagesToRemove(newList);
        }
        setImages(images.map((img) => (img.id === id ? { ...img, file } : img)));
    };

    // Function to handle removing an image
    const handleImageRemove = (id) => {
        const updatedImagesToRemove = [...imagesToRemove];
        if (typeof id === "string" && !updatedImagesToRemove.includes(id)) {
            updatedImagesToRemove.push(id);
            setImagesToRemove(updatedImagesToRemove);
        }
        const updatedImages = images.filter((img) => img.id !== id);
        setImages(updatedImages);
    };

    const handleStoreImage = (file) => {
        setLogo(file);
    };


    useEffect(() => {
        if (location.state) {
            setRowData(location.state);
            if (location.state.logo) {
                setLogo(location.state.logo);
            }
            if (location.state.images && location.state.images.length !== 0) {
                let imgs = [];

                for (let i = 0; i < location.state.images.length; i++) {
                    imgs.push({ id: location.state.images[i].id, file: location.state.images[i].image });
                }
                setImages(imgs);
            }
        }
    }, [location]);

    const handleDiscardChanges = (resetForm) => {
        resetForm();
        navigate(-1);
    };

    const handleFormSubmit = async (values, { setSubmitting }) => {
        const data = new FormData();
        data.append("name", values.name);
        data.append("owner_id", values.owner);
        data.append("address", JSON.stringify({
            line1: values.line1,
            line2: values.line2,
            landmark: values.landmark,
            city: values.city,
            district: values.district,
            state: values.state,
            country: values.country,
            zipcode: values.zipcode,
        }));
        data.append("lat", values.lat);
        data.append("lon", values.lon);
        data.append("contact_information", values.contact_information);
        data.append("business_registration_number", values.business_registration_number);
        data.append("tax_id", values.tax_id);
        data.append("trade_license_number", values.trade_license_number);
        data.append("health_safety_certificate", values.health_safety_certificate);
        data.append("fire_safety_certificate", values.fire_safety_certificate);
        data.append("other_certificates", values.other_certificates);
        data.append("link", values.link);
        data.append("about", values.about);
        data.append("year_of_establishment", values.year_of_establishment);
        data.append("owner_name", values.owner_name);
        data.append("owner_mobile_number", values.owner_mobile_number);
        data.append("images_to_delete", imagesToRemove);
        data.append("deals_in", JSON.stringify(selectedDealsIn));
        if (logo && typeof logo === "object") {
            data.append("logo", logo);
        }
        for (let i = 0; i < images.length; i++) {
            if (images[i] && typeof images[i].file === "object") {
                data.append(`images${i + 1}`, images[i].file);
            }
        }

        try {
            const response = rowData ? await patchShop({ data: data, id: rowData.id, }) : await postShop(data);
            if (response?.status === 201 || response?.status === 200) {
                toast.success(rowData ? "Shop Updated Successfully" : "Shop Added successfully!");
                setSelectedDealsIn([]);
                navigate(-1);
            } else {
                let errors = "Opps! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Opps! Something went wrong, please try again.";
            toast.error(errors);
        }
        setSubmitting(false);
    };

    const getDealsInOptions = async () => {
        try {
            const response = await getCategories();
            if (response?.status === 200) {
                setDealsInOptions(response.data.results);
            } else {
                let errors = "Opps! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Opps! Something went wrong, please try again.";
            toast.error(errors);
        }
    };

    useEffect(() => { getDealsInOptions(); }, []);
    return (<>
        <Breadcrumbs items={[
            { name: 'Home', link: '/dashboard' },
            { name: 'Bazzar', },
            { name: 'Shop', },
            { name: rowData ? 'Edit Shop' : 'Add Shop' },
        ]} />
        <div className="mt-8">
            <Formik
                initialValues={{ ...rowData, ...rowData?.address || {} } || {
                    name: "",
                    order: 0,
                    owner: "",
                    address: "",
                    contact_information: "",
                    business_registration_number: "",
                    tax_id: "",
                    trade_license_number: "",
                    health_safety_certificate: "",
                    fire_safety_certificate: "",
                    other_certificates: "",
                    link: "",
                    about: "",
                    year_of_establishment: "",
                    owner_name: "",
                    owner_mobile_number: "",
                    line1: "",
                    line2: "",
                    landmark: "",
                    city: "",
                    district: "",
                    state: "",
                    country: "",
                    zipcode: "",
                    lat: "",
                    lon: "",
                }}
                enableReinitialize
                validationSchema={Yup.object({
                    name: Yup.string().required("Please enter a name"),
                    tax_id: Yup.string().required("Tax ID is required"),
                    business_registration_number: Yup.string().required("Reg. No. is required"),
                })}
                onSubmit={handleFormSubmit}
            >
                {({ isSubmitting, setFieldValue, resetForm, values }) => (
                    <Form>
                        <div className="border border-gray-300 mt-5 p-4 rounded-lg bg-gray-100 flex flex-col md:flex-row gap-4">
                            <div className="flex flex-col w-full">
                                <div className="grid gap-2 grid-cols-1 md:grid-cols-2">
                                    {
                                        rowData ? <></> :
                                            <AsyncFormikDropdown
                                                label="User"
                                                name="owner"
                                                placeholder="Search User"
                                                apiCallFunction={async (input) => {
                                                    const response = await searchUser(input);
                                                    setUserOptions(response.data);
                                                    return response.data.map((patient) => ({
                                                        value: patient.id,
                                                        label: `${patient.name} | ${patient.phone_number} ${patient.address ? "|" + patient.address.state : ""}`,
                                                    }));
                                                }}
                                                required
                                                needSearch={true}
                                                onChange={(selectedOption) => {
                                                    const selectedPatient = userOptions.find(
                                                        (item) => item.id === selectedOption.value
                                                    );
                                                    if (selectedPatient) {
                                                        setFieldValue("owner_name", selectedPatient.name);
                                                        setFieldValue("owner_mobile_number", selectedPatient.phone_number);
                                                    }
                                                }}
                                            />
                                    }
                                    <InputField
                                        label="Shop Name"
                                        name="name"
                                        placeholder="Shop Name"
                                        required
                                    />
                                    <InputField
                                        label="Owner Name"
                                        name="owner_name"
                                        placeholder="Owner Name"
                                        required
                                    />
                                    <InputField
                                        label="Order"
                                        name="order"
                                        placeholder="0"
                                    />
                                    <InputField
                                        label="Owner Number"
                                        name="owner_mobile_number"
                                        placeholder="Owner Number"
                                        required

                                        onChange={(e) => setFieldValue('owner_mobile_number', sanitizeMobile(e.target.value))}
                                    />
                                    <InputField
                                        label="Registration Number"
                                        name="business_registration_number"
                                        type="text"
                                        placeholder="XXXXXXXXXXXX"
                                        required
                                    />
                                    <InputField
                                        label={"Tax ID"}
                                        name="tax_id"
                                        placeholder={"Enter Tax ID"}
                                        required
                                    />
                                    <InputField
                                        label={"Contact"}
                                        name="contact_information"
                                        placeholder={"XXXXXXXXXX"}

                                        onChange={(e) => setFieldValue('contact_information', sanitizeMobile(e.target.value))}
                                    />
                                    <InputField
                                        label={"Trade License Number"}
                                        name="trade_license_number"
                                        placeholder={"XXXXXXXXXX"}
                                    />
                                    <InputField
                                        label={"Year Of Est."}
                                        name="year_of_establishment"
                                        placeholder={"XXXX"}
                                    />
                                    <InputField
                                        label="Website"
                                        name="link"
                                        type="url"
                                        placeholder="https://xyz.com"
                                    />
                                </div>
                                <div className="grid gap-2 grid-cols-1">
                                    <InputField
                                        label="About"
                                        name="about"
                                        placeholder="Very nice company with 400+ members working together in a colleboration...."
                                    />

                                </div>
                                <StyledHeadingText text={"Deals In"} />
                                <div className="flex flex-wrap gap-3">
                                    {dealsInOptions.map((e) => (
                                        <div
                                            key={e.id}
                                            className={`px-4 py-2 border rounded-lg cursor-pointer transition-all duration-200 
                        ${selectedDealsIn.includes(e.id)
                                                    ? "bg-[#2fb95d] text-white border-[#2fb95d]"
                                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                                }`}
                                            onClick={() => toggleSelection(e.id)}
                                        >
                                            {e.name}
                                        </div>
                                    ))}
                                </div>
                                <div className="flex flex-col">
                                    <StyledHeadingText text={"Address"} />
                                    <div className="grid gap-2 grid-cols-1 md:grid-cols-2">
                                        <InputField
                                            label="Line 1"
                                            name="line1"
                                            type="text"
                                            placeholder="Enter Address 1"
                                            required
                                        />
                                        <InputField
                                            label="Line 2"
                                            name="line2"
                                            type="text"
                                            placeholder="Enter Address 2 (Optional)"
                                        />
                                        <InputField
                                            label="Landmark"
                                            name="landmark"
                                            type="text"
                                            placeholder="Enter Landmark (Optional)"
                                        />
                                        <InputField
                                            label="Zipcode"
                                            name="zipcode"
                                            type="text"
                                            placeholder="452001"
                                            onChange={async (value) => {
                                                setFieldValue("zipcode", value.target.value);
                                                if (value.target.value.length === 6) {
                                                    const resp = await callPincodeApi({ value: value.target.value });
                                                    applyPincodeToForm(setFieldValue, resp);
                                                }
                                            }}
                                            required
                                        />
                                        <InputField
                                            label="City"
                                            name="city"
                                            type="text"
                                            placeholder="City"
                                            required
                                        />
                                        <InputField
                                            label="District"
                                            name="district"
                                            type="text"
                                            placeholder="District"
                                            required
                                        />
                                        <InputField
                                            label="State"
                                            name="state"
                                            type="text"
                                            placeholder="State"
                                            required
                                        />
                                        <InputField
                                            label="Country"
                                            name="country"
                                            type="text"
                                            placeholder="Country"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Location Coordinates */}
                                <div className="flex flex-col mt-4">
                                    <StyledHeadingText text={"Location Coordinates"} />
                                    <div className="grid gap-2 grid-cols-1 md:grid-cols-2">
                                        <InputField
                                            label="Latitude"
                                            name="lat"
                                            type="number"
                                            step="any"
                                            placeholder="22.7196"
                                        />
                                        <InputField
                                            label="Longitude"
                                            name="lon"
                                            type="number"
                                            step="any"
                                            placeholder="75.8577"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-600 mt-2">
                                        Enter coordinates to display shop location on the map. You can get coordinates from Google Maps or other mapping services.
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-col w-full bg-white rounded-lg p-4 justify-between">
                                <div className="flex flex-col">
                                    <StyledHeadingText text={"Shop Logo"} />
                                    <div className="w-48 h-48 mb-4 mt-4">
                                        <ImagePicker
                                            preloadedImage={logo}
                                            placeholder={"Upload Store Logo"}
                                            onImageSelect={handleStoreImage}
                                        />
                                    </div>
                                    <StyledHeadingText text={"Images"} />
                                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 w-full mt-4">
                                        {images.map((img) => (
                                            <div key={img.id} className="relative w-44 h-44">
                                                {/* ImagePicker Component */}
                                                <ImagePicker
                                                    preloadedImage={img.file}
                                                    placeholder={"Upload Image"}
                                                    onImageSelect={(file) => handleImageSelect(img.id, file)}
                                                    needCoverImage={false}
                                                />
                                                {/* Remove Button */}
                                                <button
                                                    onClick={() => handleImageRemove(img.id)} // Call the remove function
                                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg hover:bg-red-600"
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={handleAddImage}
                                            className="w-44 h-44 border-2 border-dashed flex items-center justify-center text-gray-500 hover:bg-gray-100"
                                        >
                                            + Add Image
                                        </button>
                                    </div>
                                </div>

                                {/* Buttons */}
                                <div className="py-5 flex justify-end gap-6">
                                    <div>
                                        <Button
                                            type="button"
                                            className="text-primary bg-white hover:bg-white-dark font-sm rounded-lg text-lg w-full px-8 py-1.5 text-center border border-primary"
                                            onClick={() => handleDiscardChanges(resetForm)}
                                            disabled={isSubmitting}
                                        >
                                            Discard Changes
                                        </Button>
                                    </div>
                                    <div>
                                        <Button
                                            type="submit"
                                            className="text-white bg-primary hover:bg-primary-dark font-sm rounded-lg text-lg w-full px-8 py-1.5 text-center"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? "Saving..." : "Save"}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    </>);
}

export default ShopForm;